import React from "react";
import { useQuery } from "@tanstack/react-query";
import { notification, Spin, Card, Table, Tag, Space, Row, Col, Statistic, Tooltip } from "antd";
import { styled } from "styled-components";
import APICall from "../../APICall";
import EndPoints from "../../APICall/EndPoints";
import ThemeCard from "../../components/ThemeCard";

const StyledHeading = styled.div`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const StyledDescription = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 2rem;
`;

const StyledCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-head-title {
    font-size: 1.2rem;
    font-weight: 600;
  }
`;

// Utility function to convert cron pattern to human-readable format
const parseCronPattern = (cronPattern) => {
  if (!cronPattern) return "No pattern";
  
  const parts = cronPattern.split(' ');
  if (parts.length < 5) return cronPattern;
  
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  
  let description = "";
  
  // Handle minutes
  if (minute === "*") {
    description += "Every minute";
  } else if (minute.includes("/")) {
    const [start, interval] = minute.split("/");
    description += `Every ${interval} minutes`;
  } else if (minute.includes("-")) {
    const [start, end] = minute.split("-");
    description += `Minutes ${start} through ${end}`;
  } else {
    description += `At minute ${minute}`;
  }
  
  description += " ";
  
  // Handle hours
  if (hour === "*") {
    description += "of every hour";
  } else if (hour.includes("/")) {
    const [start, interval] = hour.split("/");
    description += `every ${interval} hours`;
  } else if (hour.includes("-")) {
    const [start, end] = hour.split("-");
    description += `from ${start}:00 to ${end}:00`;
  } else {
    description += `at ${hour}:00`;
  }
  
  description += " ";
  
  // Handle day of month
  if (dayOfMonth === "*") {
    description += "of every day";
  } else if (dayOfMonth.includes("/")) {
    const [start, interval] = dayOfMonth.split("/");
    description += `every ${interval} days`;
  } else if (dayOfMonth.includes("-")) {
    const [start, end] = dayOfMonth.split("-");
    description += `from day ${start} to ${end}`;
  } else {
    description += `on day ${dayOfMonth}`;
  }
  
  description += " ";
  
  // Handle month
  if (month === "*") {
    description += "of every month";
  } else if (month.includes("/")) {
    const [start, interval] = month.split("/");
    description += `every ${interval} months`;
  } else if (month.includes("-")) {
    const [start, end] = month.split("-");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    description += `from ${monthNames[start-1]} to ${monthNames[end-1]}`;
  } else {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    description += `in ${monthNames[month-1]}`;
  }
  
  description += " ";
  
  // Handle day of week
  if (dayOfWeek === "*") {
    description += "on any day of the week";
  } else if (dayOfWeek.includes("/")) {
    const [start, interval] = dayOfWeek.split("/");
    description += `every ${interval} days of the week`;
  } else if (dayOfWeek.includes("-")) {
    const [start, end] = dayOfWeek.split("-");
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    description += `on ${dayNames[start]} through ${dayNames[end]}`;
  } else {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    description += `on ${dayNames[dayOfWeek]}`;
  }
  
  return description.trim();
};

const CronInfo = () => {
  // API call to fetch cron information
  const getCronInfo = async () => {
    const response = await APICall("get", EndPoints.getCronInfo);
    return response?.data?.data || response?.data || {};
  };

  const {
    isLoading,
    error,
    data: cronData,
    refetch,
  } = useQuery({
    queryKey: ["cronInfo"],
    queryFn: getCronInfo,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  if (error) {
    notification.error({ message: error?.message || "Failed to fetch cron information" });
  }

  // Define columns for system crons
  const systemCronColumns = [
    {
      title: "Cron Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
      width: 300,
    },
    {
      title: "Cron Pattern",
      dataIndex: "cronPattern",
      key: "cronPattern",
      render: (text) => {
        if (!text) return <Tag color="gray">No pattern</Tag>;
        const readableFormat = parseCronPattern(text);
        return (
          <Tooltip title={readableFormat}>
            <Tag color="blue">{text}</Tag>
          </Tooltip>
        );
      },
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "cronPattern",
      key: "description",
      render: (text) => {
        if (!text) return "No pattern available";
        return <span style={{ fontSize: '0.9rem', color: '#666' }}>{parseCronPattern(text)}</span>;
      },
      width: 400,
    },
  ];

  // Define columns for custom crons
  const customCronColumns = [
    {
      title: "Job Key",
      dataIndex: "key",
      key: "key",
      render: (text) => <strong>{text}</strong>,
      width: 250,
    },
    {
      title: "Market Name",
      dataIndex: "marketName",
      key: "marketName",
      render: (text) => <Tag color="green">{text}</Tag>,
      width: 120,
    },
    {
      title: "Cron Pattern",
      dataIndex: "cronPattern",
      key: "cronPattern",
      render: (text) => {
        if (!text) return <Tag color="gray">No pattern</Tag>;
        const readableFormat = parseCronPattern(text);
        return (
          <Tooltip title={readableFormat}>
            <Tag color="blue">{text}</Tag>
          </Tooltip>
        );
      },
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "cronPattern",
      key: "description",
      render: (text) => {
        if (!text) return "No pattern available";
        return <span style={{ fontSize: '0.9rem', color: '#666' }}>{parseCronPattern(text)}</span>;
      },
      width: 400,
    },
    {
      title: "Job Name",
      dataIndex: "jobName",
      key: "jobName",
      render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>,
      width: 200,
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <StyledHeading>Cron Information</StyledHeading>
      <StyledDescription>
        View and monitor all scheduled cron jobs and their current status.
      </StyledDescription>

      {/* Summary Statistics */}
      {cronData?.summary && (
        <StyledCard title="Summary" style={{ marginBottom: "24px" }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Total System Jobs"
                value={cronData.summary.totalSystemJobs}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Custom Jobs"
                value={cronData.summary.totalCustomJobs}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Jobs"
                value={cronData.summary.totalJobs}
                valueStyle={{ color: '#722ed1' }}
              />
            </Col>
          </Row>
        </StyledCard>
      )}

      <Spin spinning={isLoading}>
        {/* System Crons */}
        {cronData?.systemCrons?.jobs && cronData.systemCrons.jobs.length > 0 && (
          <StyledCard 
            title={`System Crons (${cronData.systemCrons.totalJobs} jobs)`}
            style={{ marginBottom: "24px" }}
          >
            <Table
              columns={systemCronColumns}
              dataSource={cronData.systemCrons.jobs}
              rowKey={(record) => record.name}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: true }}
              size="small"
            />
          </StyledCard>
        )}

        {/* Custom Crons */}
        {cronData?.customCrons?.jobs && cronData.customCrons.jobs.length > 0 && (
          <StyledCard 
            title={`Custom Crons (${cronData.customCrons.totalCustomJobs} jobs)`}
          >
            <Table
              columns={customCronColumns}
              dataSource={cronData.customCrons.jobs}
              rowKey={(record) => record.key}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: true }}
              size="small"
            />
          </StyledCard>
        )}

        {/* Empty state */}
        {!isLoading && (!cronData || (!cronData.systemCrons?.jobs?.length && !cronData.customCrons?.jobs?.length)) && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>No cron information available</p>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default CronInfo; 